import { useQuery } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";

/**
 * Hook for fetching well registration report data
 * @param {number} selectedYear - Year to fetch report for
 * @returns {Object} Query result with data, loading state, and error
 */
export const useRegistrationReport = (selectedYear) => {
  const { getAccessTokenSilently } = useAuth0();

  const { data, isLoading, error } = useQuery(
    ["wellRegistrationReport", selectedYear],
    async () => {
      const token = await getAccessTokenSilently();
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/well-registration-report`,
        { year: selectedYear },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      enabled: !!selectedYear,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  return { data, isLoading, error };
};

/**
 * Hook for fetching wells drilldown by period
 * @param {string|null} periodStart - Start date (YYYY-MM-DD) or null to disable
 * @param {string|null} periodEnd - End date (YYYY-MM-DD, exclusive) or null to disable
 * @returns {Object} Query result with wells data, loading state, and error
 */
export const useRegistrationWellsDrilldown = (periodStart, periodEnd) => {
  const { getAccessTokenSilently } = useAuth0();

  const { data, isLoading, error } = useQuery(
    ["registrationWellsDrilldown", periodStart, periodEnd],
    async () => {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/registration-wells`,
        {
          params: { start: periodStart, end: periodEnd },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    {
      enabled: !!periodStart && !!periodEnd,
      refetchOnWindowFocus: false,
    }
  );

  return { data: data || [], isLoading, error };
};

/**
 * Hook for fetching well registration adjustments data
 * @param {number} selectedYear - Year to fetch adjustments for
 * @returns {Object} Query result with data, loading state, and error
 */
export const useRegistrationAdjustments = (selectedYear) => {
  const { getAccessTokenSilently } = useAuth0();

  const { data, isLoading, error } = useQuery(
    ["wellRegistrationAdjustments", selectedYear],
    async () => {
      const token = await getAccessTokenSilently();
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/well-registration-adjustments`,
        { year: selectedYear },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      enabled: !!selectedYear,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  return { data, isLoading, error };
};
